import { calcularPisoFrete } from './pisoFreteAntt'

//
// Regras puras da tela de MDF-e de distribuicao.
//
// Mesma divisao do resto: a tela monta filtro, chama o service e
// desenha; o que decide periodo, limite e formato fica aqui, testavel
// sem API.
//

/**
 * Teto do periodo quando o operador nao escolhe MDF-e, igual ao da API.
 * Repetido aqui de proposito: avisar antes de enviar e melhor do que
 * mostrar o 400 que a API devolveria.
 */
export const MAXIMO_DIAS_PERIODO = 31

/**
 * Janela de abertura da tela.
 *
 * Sete dias, e nao o mes corrente como nos relatorios: aqui o periodo
 * serve para achar o MDF-e que chegou hoje ou ontem ao CD, nao para
 * fechar competencia. Pico medido: 500 CT-e num dia.
 */
export const DIAS_PADRAO = 7

function doisDigitos (n) {
  return String(n).padStart(2, '0')
}

/** Data local em AAAA-MM-DD, que e o formato dos parametros da API. */
export function paraIso (data) {
  return data.getFullYear() + '-' +
         doisDigitos(data.getMonth() + 1) + '-' +
         doisDigitos(data.getDate())
}

//
// Montado a partir dos componentes LOCAIS da data, nao de
// toISOString(): depois das 21h em UTC-3 o ISO ja esta no dia seguinte,
// e a tela abriria com "hoje" errado.
//
export function periodoPadrao (hoje) {
  const fim = hoje || new Date()
  const inicio = new Date(fim.getFullYear(), fim.getMonth(), fim.getDate() - (DIAS_PADRAO - 1))
  return { inicio: paraIso(inicio), fim: paraIso(fim) }
}

/**
 * Valida o periodo com as mesmas regras da API. Devolve a mensagem de
 * erro, ou string vazia quando esta tudo certo.
 */
export function erroDoPeriodo (inicio, fim) {
  if (!inicio || !fim) return 'Informe o período.'

  const de = new Date(inicio + 'T00:00:00')
  const ate = new Date(fim + 'T00:00:00')
  if (isNaN(de.getTime()) || isNaN(ate.getTime())) return 'Período inválido.'
  if (ate < de) return 'A data final não pode ser anterior à inicial.'

  const dias = Math.round((ate - de) / 86400000) + 1
  if (dias > MAXIMO_DIAS_PERIODO) {
    return 'Período muito longo: no máximo ' + MAXIMO_DIAS_PERIODO +
      ' dias sem escolher o MDF-e.'
  }
  return ''
}

export function formatarPeso (valor) {
  return Number(valor || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 2, maximumFractionDigits: 2
  }) + ' kg'
}

export function formatarMoeda (valor) {
  return Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

export function formatarData (iso) {
  if (!iso) return ''
  const partes = String(iso).split('T')
  const data = (partes[0] || '').split('-')
  if (data.length !== 3) return String(iso)
  return data[2] + '/' + data[1] + '/' + data[0]
}

/**
 * Alvo de cobertura da fila de cadastro, em porcentagem dos CT-e que
 * cairam sem rota. Nao e regra de negocio: e o ponto a partir do qual
 * cadastrar mais uma cidade rende pouco.
 */
export const COBERTURA_ALVO = 80

/** Quantas linhas da fila aparecem antes de o operador pedir o resto. */
export const FILA_VISIVEL = 10

/**
 * Fila do cadastro de rotas: as cidades que cairam em "Sem agrupador",
 * da que recebeu mais CT-e para a que recebeu menos, com o percentual
 * acumulado.
 *
 * O acumulado e o que torna a lista acionavel. Sem ele o operador ve 98
 * cidades e nao sabe por onde comecar; com ele ve que as primeiras 20
 * cobrem 80% do volume e que a cauda de 1 e 2 CT-e pode ficar sem rota
 * sem prejuizo. Medido em producao em 2026-11: 2505 de 2781 CT-e sem
 * rota, em 98 cidades, e 20 delas cobriam 61%.
 *
 * Agrupa pelo codigo IBGE, nunca pelo nome - PACO e PAÇO DO LUMIAR
 * virariam duas linhas para a mesma cidade, e duas rotas onde deveria
 * haver uma. Cidade sem codigo entra pelo par UF + nome, que e o que
 * resta.
 *
 * Sai do grupo que a API ja mandou: nao custa requisicao nenhuma.
 */
export function filaDeCadastro (lista) {
  const grupos = (lista && lista.grupos) || []
  const porCidade = new Map()

  grupos.filter(g => g && !g.idAgrupador).forEach(g => {
    (g.ctes || []).forEach(c => {
      const chave = c.codigoIbge != null
        ? 'ibge:' + c.codigoIbge
        : 'nome:' + (c.uf || '') + '|' + (c.municipio || '')

      const cidade = porCidade.get(chave) || {
        uf: c.uf || '',
        municipio: c.municipio || '',
        codigoIbge: c.codigoIbge != null ? c.codigoIbge : null,
        ctes: 0,
        peso: 0,
        valorCarga: 0
      }
      cidade.ctes += 1
      cidade.peso += Number(c.peso || 0)
      cidade.valorCarga += Number(c.valorCarga || 0)
      porCidade.set(chave, cidade)
    })
  })

  const fila = Array.from(porCidade.values()).sort((a, b) => {
    if (b.ctes !== a.ctes) return b.ctes - a.ctes
    return String(a.municipio).localeCompare(String(b.municipio))
  })

  const total = fila.reduce((soma, c) => soma + c.ctes, 0)
  let acumulado = 0
  return fila.map(c => {
    acumulado += c.ctes
    return Object.assign({}, c, {
      acumuladoPct: total ? Math.round(1000 * acumulado / total) / 10 : 0
    })
  })
}

/**
 * Quantas cidades da fila cobrem o percentual alvo. E a frase de que o
 * operador precisa — "cadastre 20" — em vez de uma lista de 98.
 */
export function cidadesParaCobrir (fila, alvoPct) {
  const linhas = fila || []
  const alvo = alvoPct || COBERTURA_ALVO
  for (let i = 0; i < linhas.length; i++) {
    if (linhas[i].acumuladoPct >= alvo) return i + 1
  }
  return linhas.length
}

/**
 * A rodada esta em andamento?
 *
 * Exige o id tambem, e nao so finalizadoEm nulo: a API responde 204 sem
 * corpo quando nao ha rodada, e o axios entrega isso como string vazia.
 * Checar apenas `!rodada.finalizadoEm` daria verdadeiro para a resposta
 * vazia, e a tela abriria em modo rodada sem rodada nenhuma.
 */
export function rodadaEmAndamento (rodada) {
  return !!(rodada && rodada.idLote && !rodada.finalizadoEm)
}

/**
 * Linha de identificacao da rodada: "Rodada #42 · aberta em 03/11/2026 ·
 * 2781 CT-e".
 *
 * O numero vem primeiro porque e o que o operador usa para falar da
 * rodada com outra pessoa.
 */
export function resumoDaRodada (rodada) {
  if (!rodada || !rodada.idLote) return ''

  const partes = ['Rodada #' + rodada.idLote]

  const quando = formatarData(rodada.criadoEm)
  if (quando) partes.push('aberta em ' + quando)

  if (rodada.finalizadoEm) {
    const fim = formatarData(rodada.finalizadoEm)
    if (fim) partes.push('finalizada em ' + fim)
  }

  const total = rodada.lista && rodada.lista.totalCtes
  if (total != null) partes.push(total + ' CT-e')

  return partes.join(' · ')
}

/**
 * Situacao da comparacao com o piso, nos mesmos codigos que a API grava
 * em distribuicao_mdfe.piso_situacao.
 *
 * NAO_CALCULAVEL existe como estado proprio, e nao como "acima": a
 * tabela B da 6.084/2026 (apenas unidade tratora) so tem coeficiente a
 * partir de 4 eixos, e o caminhao rigido de 2 ou 3 eixos e justamente o
 * que faz entrega urbana. Mostrar verde ali seria afirmar uma conta que
 * nao foi feita.
 */
export const PISO_NAO_CALCULAVEL = 0
export const PISO_ACIMA = 1
export const PISO_ABAIXO = 2

/**
 * Compara o frete da perna final com o piso minimo da ANTT.
 *
 * Nao reimplementa a conta: chama o calcularPisoFrete de
 * pisoFreteAntt.js, que e de onde a Calculadora de Piso tambem le. A
 * tabela de coeficientes tem que ter um dono so - duas copias viram uma
 * desatualizada em silencio na proxima resolucao.
 *
 * Tabela A ou B, nunca C ou D: C e D sao de Alto Desempenho, definido
 * como carga mais descarga em ate 3 horas, e uma rota que entrega em 8
 * cidades nao cabe nisso. A escolha entre A e B e ter ou nao composicao
 * veicular, que vem do cadastro do veiculo.
 *
 * Tipo de carga fixo em CARGA_GERAL, igual ao tpCarga 05 que o payload
 * do MDF-e declara: uma rota leva carga de varios CT-e e nao ha produto
 * unico.
 */
export function conferirPiso ({ composicaoVeicular, eixos, distanciaKm, valorFrete }) {
  const tabela = composicaoVeicular ? 'A' : 'B'
  const resultado = calcularPisoFrete({
    tabela, tipoCarga: 'CARGA_GERAL', eixos, distanciaKm
  })

  if (resultado.erro) {
    return {
      situacao: PISO_NAO_CALCULAVEL,
      piso: null,
      faltam: 0,
      motivo: resultado.erro,
      base: baseDoCalculo(tabela, eixos, distanciaKm, null, null)
    }
  }

  const piso = Math.round(resultado.piso * 100) / 100
  const frete = Number(valorFrete || 0)
  const abaixo = frete < piso

  return {
    situacao: abaixo ? PISO_ABAIXO : PISO_ACIMA,
    piso,
    faltam: abaixo ? Math.round((piso - frete) * 100) / 100 : 0,
    motivo: '',
    base: baseDoCalculo(tabela, eixos, distanciaKm, resultado.ccd, resultado.cc)
  }
}

/**
 * A base por extenso, para ficar gravada junto com o numero.
 *
 * Sem ela o piso e um valor sem procedencia: a tabela muda por
 * resolucao, e recalcular no ano que vem da outro numero. Cortada em
 * 120 para caber na coluna.
 */
function baseDoCalculo (tabela, eixos, distanciaKm, ccd, cc) {
  let texto = '6.084/2026 ' + tabela + ' CARGA_GERAL ' +
    (eixos || '?') + 'eixos ' + (Number(distanciaKm) || 0) + 'km'
  if (ccd != null && cc != null) {
    texto += ' ccd=' + ccd + ' cc=' + cc
  }
  return texto.substring(0, 120)
}

/** As chaves de acesso dos CT-e de um grupo, na ordem em que aparecem. */
export function chavesDoGrupo (grupo) {
  return ((grupo && grupo.ctes) || [])
    .map(c => c && c.chaveAcesso)
    .filter(Boolean)
}

/**
 * Totais do que esta marcado dentro de um grupo.
 *
 * Nao reusa os totais do grupo: aqueles sao do grupo inteiro, e a carga
 * de uma rota pode nao caber num caminhao - o operador manda parte agora
 * e o resto depois. O numero que decide se cabe e o da selecao.
 *
 * Cidades conta pelo codigo IBGE, como no resumo do grupo, porque e esse
 * numero que diz quantos blocos de descarregamento o MDF-e vai ter.
 */
export function totaisDaSelecao (grupo, selecionadas) {
  const marcadas = new Set(selecionadas || [])
  const cidades = new Set()
  let ctes = 0
  let peso = 0
  let valorCarga = 0

  ;((grupo && grupo.ctes) || []).forEach(c => {
    if (!c || !marcadas.has(c.chaveAcesso)) return
    ctes += 1
    peso += Number(c.peso || 0)
    valorCarga += Number(c.valorCarga || 0)
    if (c.codigoIbge != null) cidades.add(c.codigoIbge)
    else if (c.municipio) cidades.add(c.municipio)
  })

  return { ctes, peso, valorCarga, cidades: cidades.size }
}

/**
 * Agrupa a resposta de /embarcados por chave de CT-e.
 *
 * Varias linhas para a mesma chave e resultado esperado, nao
 * duplicacao: o mesmo CT-e em dois MDF-e e transbordo, que e legal. Por
 * isso o indice guarda lista, e nao uma linha so.
 */
export function indiceDeEmbarques (embarques) {
  const indice = {}
  ;(embarques || []).forEach(e => {
    if (!e || !e.chaveAcesso) return
    if (!indice[e.chaveAcesso]) indice[e.chaveAcesso] = []
    indice[e.chaveAcesso].push(e)
  })
  return indice
}

/**
 * O aviso de ja embarcado, em uma linha.
 *
 * Diz em qual MDF-e, porque "ja embarcou" sem o numero obriga o operador
 * a ir procurar. Com mais de um, diz quantos: ali e transbordo, e ele
 * precisa decidir, nao ser impedido.
 */
export function resumoDoEmbarque (embarques) {
  const lista = embarques || []
  if (!lista.length) return ''

  const primeiro = lista[0]
  const onde = primeiro.mdfeNumero
    ? 'MDF-e ' + primeiro.mdfeNumero
    : (primeiro.mdfeChave ? 'MDF-e ' + primeiro.mdfeChave.slice(-9) : 'outro MDF-e')

  if (lista.length === 1) {
    return 'já embarcou no ' + onde +
      (primeiro.nomeAgrupador ? ' (' + primeiro.nomeAgrupador + ')' : '')
  }
  return 'já embarcou em ' + lista.length + ' MDF-e, o primeiro ' + onde
}

/**
 * Resumo do grupo para o cabecalho: "12 CT-e · 3 cidades".
 *
 * A contagem de cidades sai dos proprios CT-e, pelo codigo IBGE - e o
 * numero que explica por que a rota existe.
 */
export function resumoDoGrupo (grupo) {
  const ctes = (grupo && grupo.ctes) || []
  const cidades = new Set()
  ctes.forEach(c => {
    if (c.codigoIbge != null) cidades.add(c.codigoIbge)
    else if (c.municipio) cidades.add(c.municipio)
  })

  const qtd = (grupo && grupo.quantidade) || ctes.length
  const partes = [qtd + (qtd === 1 ? ' CT-e' : ' CT-e')]
  if (cidades.size) {
    partes.push(cidades.size + (cidades.size === 1 ? ' cidade' : ' cidades'))
  }
  return partes.join(' · ')
}
